import React from "react";
import { Habit, Day, Category } from "../types";
import { HabitButton } from "./HabitButton";
import { AddHabitButton } from "./AddHabitButton";
import { CategorySection } from "./CategorySection";
import { getSortedHabits } from "../utils/habitUtils";

interface HabitsSectionProps {
  habits: Habit[];
  days: Day[];
  categories?: Category[];
  selectedDay: string;
  greyOutEnabled: boolean;
  categoriesEnabled: boolean;
  sortOrder: "alphabetical" | "score";
  collapsedCategories: number[];
  pressStartTime: number | null;
  pressScale: number;
  pressedItemId: number | null;
  onHabitClick: (habit: Habit) => void;
  onAddHabit: () => void;
  onCategoryToggle: (categoryId: number) => void;
  onPressStart: (habit: Habit, event: React.MouseEvent | React.TouchEvent) => void;
  onPressMove: (event: React.MouseEvent | React.TouchEvent) => void;
  onPressEnd: (habit: Habit) => void;
  onPressCancel: () => void;
}

export const HabitsSection: React.FC<HabitsSectionProps> = ({
  habits,
  days,
  categories,
  selectedDay,
  greyOutEnabled,
  categoriesEnabled,
  sortOrder,
  collapsedCategories,
  pressStartTime,
  pressScale,
  pressedItemId,
  onHabitClick,
  onAddHabit,
  onCategoryToggle,
  onPressStart,
  onPressMove,
  onPressEnd,
  onPressCancel,
}) => {
  const sortedHabits = getSortedHabits(habits, sortOrder, categoriesEnabled, categories);

  return (
    <div style={{ display: "flex", flexWrap: "wrap", marginTop: "10px" }}>
      {categoriesEnabled ? (
        <>
          {/* Uncategorized habits */}
          {sortedHabits
            .filter(habit => !habit.categoryId && (!habit.archived || days.some(day => day.date === selectedDay && day.habits.includes(habit.id))))
            .map(habit => (
              <HabitButton
                key={habit.id}
                habit={habit}
                days={days}
                selectedDay={selectedDay}
                greyOutEnabled={greyOutEnabled}
                pressStartTime={pressStartTime}
                pressScale={pressScale}
                pressedItemId={pressedItemId}
                onPressStart={onPressStart}
                onPressMove={onPressMove}
                onPressEnd={onPressEnd}
                onPressCancel={onPressCancel}
                onClick={onHabitClick}
              />
            ))}

          {/* Categorized habits */}
          {categories?.map(category => (
            <CategorySection
              key={category.id}
              category={category}
              habits={sortedHabits}
              days={days}
              selectedDay={selectedDay}
              greyOutEnabled={greyOutEnabled}
              isCollapsed={collapsedCategories.includes(category.id)}
              pressStartTime={pressStartTime}
              pressScale={pressScale}
              pressedItemId={pressedItemId}
              onCategoryToggle={onCategoryToggle}
              onHabitClick={onHabitClick}
              onPressStart={onPressStart}
              onPressMove={onPressMove}
              onPressEnd={onPressEnd}
              onPressCancel={onPressCancel}
            />
          ))}
        </>
      ) : (
        /* Non-categorized view */
        sortedHabits
          .filter((habit) => !habit.archived || days.some((day) => day.date === selectedDay && day.habits.includes(habit.id)))
          .map((habit) => (
            <HabitButton
              key={habit.id}
              habit={habit}
              days={days}
              selectedDay={selectedDay}
              greyOutEnabled={greyOutEnabled}
              pressStartTime={pressStartTime}
              pressScale={pressScale}
              pressedItemId={pressedItemId}
              onPressStart={onPressStart}
              onPressMove={onPressMove}
              onPressEnd={onPressEnd}
              onPressCancel={onPressCancel}
              onClick={onHabitClick}
            />
          ))
      )}

      {/* Add habit button */}
      {habits.length < 30 && (
        <AddHabitButton onClick={onAddHabit} />
      )}
    </div>
  );
};